// The three message classes (design manual Ch08 §2.6). Every message on a
// surface is one of these, and each class has ONE look, so a user learns once
// what each look means:
//
//   warning   — this failed or was refused, and what to do. Red outline box,
//               red text. It sits AT THE CONTROL it answers (§2.5), never only
//               in a banner at the top of the page.
//   platform  — the platform's own state: the index rebuilding, subscription
//               standing, a load running, a newer build. Neutral or amber box,
//               never red. Top of the page, or a toast.
//   guidance  — what you can do next, what this is, an empty state. Secondary
//               text on a soft surface, no coloured border.
//   outcome   — the result of the action just taken, when the changed thing
//               itself cannot carry it: "Imported 1,234 rows into Data". Green
//               outline, success text. This is §2.5's outcome, not a fourth
//               message class; it appears where the action was taken and says
//               what happened, in numbers.
//
// Before 2026-09-21 the SPA carried 57 hand-rolled variants of these boxes
// (border opacity, background, radius and padding all chosen per call site),
// so nothing kept the looks apart. This is the one place they are drawn.
import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from '@/lib/utils';

export type MessageKind = 'warning' | 'platform' | 'guidance' | 'outcome';

export interface MessageProps extends HTMLAttributes<HTMLDivElement> {
  kind: MessageKind;
  /** Platform messages only: `info` is the neutral state, `warning` the amber one. */
  tone?: 'info' | 'warning';
  /** `compact` for a row or a chip; `normal` for a block. */
  density?: 'normal' | 'compact';
  children: ReactNode;
}

const LOOK: Record<MessageKind, string> = {
  warning: 'border border-tres-error/60 bg-tres-error/5 text-tres-error',
  platform: '', // set by tone
  guidance: 'bg-muted/50 text-tres-text-secondary',
  outcome: 'border border-tres-success/60 bg-tres-success/10 text-tres-text-primary',
};

const PLATFORM_TONE = {
  info: 'border border-tres-info/50 bg-tres-info/10 text-tres-text-primary',
  warning: 'border border-tres-warning/50 bg-tres-warning/10 text-tres-text-primary',
};

const DENSITY = {
  normal: 'rounded-md px-3 py-2 text-sm',
  compact: 'rounded px-2 py-1 text-xs',
};

/** The classes for a message look, for an element that must stay what it is
 *  (an <li>, a <label>, a row that also lays out its children). New code uses
 *  <Message>; this exists so a swept call site keeps its element and gains the
 *  look. */
// eslint-disable-next-line react-refresh/only-export-components -- the class recipe is exported beside the component
export function messageClass(kind: MessageKind, density: 'normal' | 'compact' = 'normal', tone: 'info' | 'warning' = 'info'): string {
  const look = kind === 'platform' ? PLATFORM_TONE[tone] : LOOK[kind];
  return cn(DENSITY[density], look);
}

export function Message({ kind, tone = 'info', density = 'normal', className, children, ...rest }: MessageProps) {
  const look = kind === 'platform' ? PLATFORM_TONE[tone] : LOOK[kind];
  // A refusal interrupts; a platform state informs; guidance is plain content.
  const role = kind === 'warning' ? 'alert' : kind === 'platform' ? 'status' : undefined;
  return (
    <div role={role} className={cn(DENSITY[density], look, className)} {...rest}>
      {children}
    </div>
  );
}
